import React, { Suspense, lazy } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Link } from 'react-router-dom';
import { UserCheck, BookOpen, User, Send, BarChart2, UserCog } from 'lucide-react';
import Loader from '../../../components/common/Loader';

// Step 1: Define card data in a structured object for cleaner, more maintainable code.
const cardData = {
  mentor: [
    { to: "/dashboard/mentor/approvals", Icon: UserCheck, title: "Student Approvals", description: "Review and approve new student accounts.", color: "green" },
    { to: "/dashboard/mentor/manage-courses/upload", Icon: BookOpen, title: "Create/Edit Courses", description: "Add new courses or update existing ones.", color: "blue" },
    { to: "/dashboard/mentor/students", Icon: User, title: "Students List", description: "View and manage your student list.", color: "gray" },
    { to: "/dashboard/mentor/manage-courses", Icon: BookOpen, title: "Manage Courses List", description: "View all your created courses.", color: "purple" },
  ],
  admin: [
    { to: "/dashboard/mentor/approvals", Icon: UserCheck, title: "Student Approvals", description: "Review and approve all student accounts.", color: "green" },
    { to: "/dashboard/admin/manage-courses", Icon: BookOpen, title: "Manage Courses", description: "View, edit, or delete all courses.", color: "blue" },
    { to: "/dashboard/admin/students", Icon: User, title: "Manage Users", description: "View and manage all users and roles.", color: "gray" },
    { to: "/dashboard/admin/create-blog", Icon: Send, title: "Create Blog", description: "Write and publish new blog posts.", color: "indigo" },
    { to: "/dashboard/admin/mentors", Icon: UserCog, title: "Manage Mentors", description: "Oversee all mentor accounts and permissions.", color: "purple" },
    { to: "#", Icon: BarChart2, title: "Analytics", description: "View performance and enrollment data (coming soon).", color: "red" },
  ],
};

// A reusable component for rendering the grid of cards.
const CardGrid: React.FC<{ cards: typeof cardData.mentor }> = ({ cards }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {cards.map(({ to, Icon, title, description, color }) => (
      <Link
        key={title}
        to={to}
        className="group bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col items-start gap-4"
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </Link>
    ))}
  </div>
);

// Step 2: Lazy load the specific views for better performance.
const MentorView = lazy(() => Promise.resolve({ default: () => <CardGrid cards={cardData.mentor} /> }));
const AdminView = lazy(() => Promise.resolve({ default: () => <CardGrid cards={cardData.admin} /> }));

const MentorDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <p className="text-center mt-10 text-gray-500">You are not logged in.</p>;
  }

  const isMentor = user.role === 'mentor' && !user.isAdmin;
  const isAdmin = user.isAdmin;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-gray-800">
          Welcome back, <span className="text-blue-600">{user.name}</span>! 👋
        </h1>
        <p className="text-gray-500 mt-1">Here is your control center for managing your students and courses.</p>
      </header>

      {/* Step 3: Use Suspense to handle the lazy loading of the views */}
      <Suspense fallback={<Loader />}>
        {isMentor && <MentorView />}
        {isAdmin && <AdminView />}
      </Suspense>
    </div>
  );
};

export default MentorDashboard;